<script lang="ts">
	import PageHeader from '$lib/components/PageHeader.svelte';
	import Pill from '$lib/components/Pill.svelte';
	import {
		Brain,
		MessageSquareCode,
		GitPullRequest,
		Rocket,
		ArrowLeft,
		ArrowRight
	} from '@lucide/svelte';

	// The concrete timespan from the story.
	const timeline = [
		{
			time: '12:00',
			icon: Brain,
			label: 'Timer starts',
			body: 'A developer opens a fresh agent session and starts a Moment Tally timer, labeled for the work at hand.',
			tone: 'accent'
		},
		{
			time: '12:00–12:45',
			icon: MessageSquareCode,
			label: 'Think · prompt · review',
			body: 'Thinking through the approach, prompting the agent, and reviewing the diffs it produces — all one continuous, labeled span.',
			tone: 'muted'
		},
		{
			time: '12:45',
			icon: GitPullRequest,
			label: 'Deliverables merged',
			body: 'The knowledge-graph feature lands: commits and a PR merged into the default branch.',
			tone: 'accent'
		},
		{
			time: '12:52',
			icon: Rocket,
			label: 'Live in production',
			body: 'Seven minutes later the change is deployed. The 52-minute span now maps cleanly onto shipped value.',
			tone: 'accent'
		}
	];

	const outcomes = [
		{
			stat: '52 min',
			label: 'One labeled span, start to production',
			body: 'From the first prompt to a live deploy — a single, queryable record instead of a vague afternoon.'
		},
		{
			stat: '1 join',
			label: 'Time ⋈ source control',
			body: "Labels line up with source control's record of commits and PRs, so hours attach to outcomes automatically."
		},
		{
			stat: '0 guesswork',
			label: 'Records & invoicing',
			body: 'Internal record-keeping and client invoicing derive from data, not memory or reconstructed timesheets.'
		}
	];
</script>

<svelte:head>
	<title>Time, joined to what shipped — Moment Tally case study</title>
	<meta
		name="description"
		content="How StreetFortress developers use Moment Tally to correlate time spent thinking, prompting, and reviewing agent sessions with real commits and PRs — turning labeled timespans into records and invoices tied to delivered value."
	/>
</svelte:head>

<PageHeader
	eyebrow="Case study · StreetFortress"
	title="Time, joined to what shipped"
	lead="At SFI, developers juggle agent sessions all day. Moment Tally turns the time spent thinking, prompting, and reviewing into labeled spans that line up with real commits and PRs — so records and invoices follow delivered value, not guesswork."
/>

<div class="mx-auto max-w-4xl px-5 py-16">
	<a
		href="/case-study"
		class="inline-flex items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-accent"
	>
		<ArrowLeft size={15} /> All case studies
	</a>

	<!-- The challenge -->
	<section class="mt-10">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">The challenge</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			Agent work is real work — and it's hard to account for
		</h2>
		<p class="mt-4 leading-relaxed text-fg-muted">
			An agent session isn't idle time. It's thinking through an approach, writing prompts, and
			reviewing generated code — often across several repos at once, interrupted and resumed. Filed
			into one rigid category, that effort disappears. SFI needed to see it in the same terms as the
			work it produced: <span class="text-fg">which repo, what kind of work, and what shipped.</span
			>
		</p>
	</section>

	<!-- The approach: labels -->
	<section class="mt-14">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">The approach</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			Label the span, not the folder
		</h2>
		<p class="mt-4 leading-relaxed text-fg-muted">
			Every session gets key: value labels that mirror how work is organized in source control. One
			span, dimensioned across every axis that matters:
		</p>
		<div class="card mt-6 flex flex-wrap items-center gap-2 p-6">
			<Pill tone="red">repo: sfi/sfi-website</Pill>
			<Pill tone="blue">feat: kb-knowledge-graph</Pill>
			<Pill tone="green">type: review</Pill>
			<span class="ml-auto font-mono text-xs text-fg-faint">one timespan · many axes</span>
		</div>
		<p class="mt-4 text-sm leading-relaxed text-fg-muted">
			The join only works because the schema is disciplined: <span class="font-mono text-fg"
				>repo</span
			>
			mirrors the code host's exact naming, each key holds one fact, and values come from a small, stable
			vocabulary. The principles behind that are written up in
			<a class="text-accent hover:underline" href="/docs/labels"
				>What makes a good labeling schema</a
			>.
		</p>
	</section>

	<!-- The timeline -->
	<section class="mt-14">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">Anatomy of a span</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">Noon to production</h2>

		<ol class="mt-8 space-y-0">
			{#each timeline as t, i (t.time)}
				<li class="relative flex gap-5 pb-8 last:pb-0">
					<!-- rail -->
					{#if i < timeline.length - 1}
						<span class="absolute left-[19px] top-10 h-full w-px bg-border" aria-hidden="true"
						></span>
					{/if}
					<div
						class="relative z-10 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border {t.tone ===
						'accent'
							? 'border-accent/40 bg-accent/10 text-accent'
							: 'border-border bg-surface/60 text-fg-muted'}"
					>
						<t.icon size={18} />
					</div>
					<div class="pt-0.5">
						<div class="flex items-center gap-3">
							<span class="tnum font-mono text-sm font-semibold text-fg">{t.time}</span>
							<span class="font-display font-bold">{t.label}</span>
						</div>
						<p class="mt-1 text-sm leading-relaxed text-fg-muted">{t.body}</p>
					</div>
				</li>
			{/each}
		</ol>

		<!-- span bar -->
		<div class="card mt-4 p-5">
			<div class="mb-2 flex items-center justify-between font-mono text-xs text-fg-faint">
				<span>12:00</span>
				<span>12:45 · merged</span>
				<span>12:52 · prod</span>
			</div>
			<div class="relative h-3 overflow-hidden rounded-full bg-surface">
				<div
					class="absolute inset-y-0 left-0 rounded-full"
					style="width: 86.5%; background: linear-gradient(90deg, var(--indigo), var(--blue))"
				></div>
				<div
					class="absolute inset-y-0 rounded-full"
					style="left: 86.5%; width: 13.5%; background: linear-gradient(90deg, var(--blue), var(--green))"
				></div>
			</div>
			<p class="mt-3 text-sm text-fg-muted">
				A single 52-minute span — <span class="text-fg">repo: sfi/sfi-website</span>,
				<span class="text-fg">feat: kb-knowledge-graph</span> — bounded on one end by the first prompt
				and on the other by a live deploy.
			</p>
		</div>
	</section>

	<!-- The outcome -->
	<section class="mt-16">
		<p class="font-mono text-xs uppercase tracking-widest text-accent">The outcome</p>
		<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight">
			Records and invoices that write themselves
		</h2>
		<div class="mt-6 grid gap-4 sm:grid-cols-3">
			{#each outcomes as o (o.label)}
				<div class="card p-6">
					<p class="font-display text-3xl font-extrabold text-grad">{o.stat}</p>
					<p class="mt-2 font-semibold">{o.label}</p>
					<p class="mt-1 text-sm leading-relaxed text-fg-muted">{o.body}</p>
				</div>
			{/each}
		</div>
		<p class="mt-6 leading-relaxed text-fg-muted">
			Because each span is labeled the way the code is organized, Moment Tally's exports join
			directly against commits and PRs. Streamlining internal record-keeping and client invoicing
			stops being a monthly reconstruction and becomes a query: associate timespans with delivered
			value, and bill for exactly what was shipped.
		</p>
	</section>

	<!-- pull quote -->
	<blockquote class="card relative mt-12 overflow-hidden p-8 sm:p-10">
		<p class="font-display text-xl font-bold leading-snug sm:text-2xl">
			“A timespan starts at noon. The deliverables merge at 12:45 and are live at 12:52. That's not
			a timesheet entry — it's <span class="text-grad">proof of what we delivered</span>.”
		</p>
		<footer class="mt-4 font-mono text-xs uppercase tracking-widest text-fg-faint">
			StreetFortress Industries · engineering
		</footer>
	</blockquote>

	<!-- CTA -->
	<section class="mt-12 text-center">
		<div class="flex flex-wrap justify-center gap-3">
			<a
				href="/docs#install"
				class="btn-accent rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105"
			>
				Get Moment Tally
			</a>
			<a
				href="/case-study/parallel-agent-loops"
				class="inline-flex items-center gap-1.5 rounded-full border border-border-strong px-6 py-3 text-sm font-medium text-fg-muted transition-colors hover:border-accent hover:text-fg"
			>
				Next: staying coherent across parallel loops <ArrowRight size={15} />
			</a>
		</div>
	</section>
</div>
